import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  Platform,
} from 'react-native';
import Text from '../../components/text';
import { useRoute } from '@react-navigation/native';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import moment from 'moment';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import MarkAsComplete from '../../components/markAsComplete/MarkAsComplete';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import { showErrorToast } from '../tenants/tenantOnboarding/helpers';
import { AntDesign, MaterialCommunityIcons } from '@expo/vector-icons';
import { SHADOWS, LAYOUT } from '../../constants/theme';
import { SetupSidebar, usePropertyRouteParams, PropertyBreadcrumb, PropertyHeaderBanner, PropertyStickyFooter } from './sharedComponents';

/* ─── Yes / No toggle (2 options) ──────────────────────────────── */
const YesNo = ({ value, onChange, boolMode = true }) => {
  const isYes = boolMode ? value === true : value === 'Yes';
  const isNo = boolMode ? value === false : value === 'No';
  return (
    <View style={yn.row}>
      <TouchableOpacity
        style={[yn.btn, isYes && yn.btnActive]}
        onPress={() => onChange(boolMode ? true : 'Yes')}
        activeOpacity={0.8}
      >
        <Text style={[yn.txt, isYes && yn.txtActive]}>Yes</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[yn.btn, isNo && yn.btnActive]}
        onPress={() => onChange(boolMode ? false : 'No')}
        activeOpacity={0.8}
      >
        <Text style={[yn.txt, isNo && yn.txtActive]}>No</Text>
      </TouchableOpacity>
    </View>
  );
};
const yn = StyleSheet.create({
  row: { flexDirection: 'row', gap: 8 },
  btn: { paddingHorizontal: 20, paddingVertical: 9, borderRadius: 8, borderWidth: 1.5, borderColor: '#D1D5DB', backgroundColor: '#fff' },
  btnActive: { backgroundColor: '#6C2BD9', borderColor: '#6C2BD9' },
  txt: { fontSize: 14, fontWeight: '600', color: '#374151' },
  txtActive: { color: '#fff' },
});

/* ─── Yes / No / NA toggle (3 options) ─────────────────────────── */
const YesNoNA = ({ value, onChange }) => {
  const opts = [
    { label: 'Yes', val: true },
    { label: 'No', val: false },
    { label: 'NA', val: 'NA' },
  ];
  return (
    <View style={yna.row}>
      {opts.map(({ label, val }) => {
        const active = value === val || (val === 'NA' && value === 'NA');
        return (
          <TouchableOpacity
            key={label}
            style={[yna.btn, active && yna.btnActive]}
            onPress={() => onChange(val)}
            activeOpacity={0.8}
          >
            <Text style={[yna.txt, active && yna.txtActive]}>{label}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};
const yna = StyleSheet.create({
  row: { flexDirection: 'row', gap: 6 },
  btn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8, borderWidth: 1.5, borderColor: '#D1D5DB', backgroundColor: '#fff' },
  btnActive: { backgroundColor: '#6C2BD9', borderColor: '#6C2BD9' },
  txt: { fontSize: 13, fontWeight: '600', color: '#374151' },
  txtActive: { color: '#fff' },
});



/* ─── Detector Card ─────────────────────────────────────────────── */
const DetectorCard = ({
  isMobile,
  iconName,
  iconColor,
  iconBg,
  title,
  question,
  value,
  onChange,
  showDate,
  dateValue,
  onDateChange,
}) => (
  <View style={[dc.card, isMobile && dc.cardMobile]}>
    {/* Header */}
    <View style={dc.headerRow}>
      <View style={[dc.iconWrap, { backgroundColor: iconBg }]}>
        <MaterialCommunityIcons name={iconName} size={18} color={iconColor} />
      </View>
      <Text style={dc.title}>{title}</Text>
    </View>

    {/* Question */}
    <Text style={dc.question}>{question}</Text>

    {/* Toggle */}
    <YesNo value={value} onChange={onChange} boolMode={true} />

    {/* Date input (if Yes) */}
    {showDate && (
      <View style={dc.dateWrap}>
        <Text style={dc.dateLabel}>Enter the date you last tested the detectors</Text>
        <TextInputComponent
          onChangeText={onDateChange}
          isDate
          date={dateValue}
          maxDate={new Date()}
        />
      </View>
    )}
  </View>
);
const dc = StyleSheet.create({
  card: { backgroundColor: '#fff', borderRadius: 14, padding: 18, marginBottom: 16, ...SHADOWS.sm },
  cardMobile: { padding: 14 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 },
  iconWrap: { width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 15, fontWeight: '700', color: '#111827', flex: 1 },
  question: { fontSize: 13, color: '#374151', marginBottom: 12, lineHeight: 20 },
  dateWrap: { marginTop: 16 },
  dateLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 8 },
});

/* ─── Habitation row (inline Yes/No/NA) ────────────────────────── */
const HabitationRow = ({ label, required, value, onChange, isLast }) => (
  <View style={[hr.row, !isLast && hr.border]}>
    <Text style={hr.label}>
      {label}
      {required && <Text style={hr.star}> *</Text>}
    </Text>
    <YesNoNA value={value} onChange={onChange} />
  </View>
);
const hr = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 14, flexWrap: 'wrap', gap: 10 },
  border: { borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  label: { fontSize: 13, fontWeight: '600', color: '#374151', flex: 1, minWidth: 120 },
  star: { color: '#EF4444' },
});

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Requirements = ({ navigation }) => {
  const routeParams = usePropertyRouteParams();
  const route = useRoute();
  const activePropertyId = routeParams.id;
  const propertyDisplayName = routeParams.name || 'Property';
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  /* ── State (unchanged from original) ── */
  const [state, setState] = useState({
    is_smoke_detector_available_on_every_floor: 'No',
    is_carbon_monoxide_detector_available_in_every_room: '',
    is_cleaned_to_professional_standard: '',
    is_plumbing_checked: '',
    appliances_checked: '',
    carbon_monoxide_detector_testing_date: null,
    smoke_detector_testing_date: null,
  });

  useEffect(() => {
    if (activePropertyId) {
      _getRequirements(activePropertyId);
    }
  }, [activePropertyId]);

  /* ── API calls (unchanged) ── */
  const _getRequirements = (id) => {
    ajax
      .get(Urls.Properties + '/' + id)
      .then(({ data }) => {
        if (data.status) {
          if (data?.payload) {
            let Apidata = data?.payload;
            setState({
              ...state,
              is_smoke_detector_available_on_every_floor: Apidata?.is_smoke_detector_available_on_every_floor,
              is_carbon_monoxide_detector_available_in_every_room: Apidata?.is_carbon_monoxide_detector_available_in_every_room,
              is_plumbing_checked: Apidata?.is_plumbing_checked,
              is_cleaned_to_professional_standard: Apidata?.is_cleaned_to_professional_standard,
              appliances_checked: Apidata?.appliances_checked,
              smoke_detector_testing_date: data?.payload?.smoke_detector_testing_date || null,
              carbon_monoxide_detector_testing_date: data?.payload?.carbon_monoxide_detector_testing_date || null,
            });
          }
        }
      })
      .catch((error) => console.log('error', error));
  };

  const _onSave = () => {
    const today = moment().endOf('day');
    if (
      state?.is_smoke_detector_available_on_every_floor &&
      state?.smoke_detector_testing_date &&
      moment(state.smoke_detector_testing_date).isAfter(today)
    ) {
      showErrorToast('Smoke detector test date cannot be in the future.');
      return;
    }
    if (
      state?.is_carbon_monoxide_detector_available_in_every_room &&
      state?.carbon_monoxide_detector_testing_date &&
      moment(state.carbon_monoxide_detector_testing_date).isAfter(today)
    ) {
      showErrorToast('Carbon monoxide detector test date cannot be in the future.');
      return;
    }

    const requestBody = {
      is_smoke_detector_available_on_every_floor: state?.is_smoke_detector_available_on_every_floor,
      is_carbon_monoxide_detector_available_in_every_room: state?.is_carbon_monoxide_detector_available_in_every_room,
      smoke_detector_testing_date: state?.is_smoke_detector_available_on_every_floor ? state?.smoke_detector_testing_date : null,
      carbon_monoxide_detector_testing_date: state?.is_carbon_monoxide_detector_available_in_every_room ? state?.carbon_monoxide_detector_testing_date : null,
      is_cleaned_to_professional_standard: state?.is_cleaned_to_professional_standard,
      is_plumbing_checked: state?.is_plumbing_checked,
      appliances_checked: state?.appliances_checked,
    };

    ajax
      .post(Urls.Requirements + '/' + activePropertyId, requestBody)
      .then(({ data }) => {
        if (data.status) {
          navigation.navigate('PropertySection', { id: activePropertyId, name: propertyDisplayName });
        }
      })
      .catch((error) => console.log('error', error));
  };

  const _handleChange = (e) => setState({ ...state, [e.label]: e.value });

  /* ════════════════════════════
     MOBILE LAYOUT
  ════════════════════════════ */
  if (isMobile) {
    return (
      <View style={ms.root}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={ms.scroll}
          showsVerticalScrollIndicator={false}
        >
          {/* Setup Progress */}

          {/* Header card */}
          <View style={ms.headerCard}>
            <View style={ms.headerIcon}>
              <MaterialCommunityIcons name="clipboard-list-outline" size={20} color="#6C2BD9" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={ms.headerTitle}>Property Requirements</Text>
              <Text style={ms.headerSub}>Please complete all safety and habitation rules for this property.</Text>
            </View>
          </View>

          {/* Mark as Complete */}
          <View style={ms.completeCard}>
            <MarkAsComplete
              label={'Property requirements'}
              flowID={route?.params?.id}
              flowtype={route?.params?.key}
              isComplete={route?.params?.status === 2}
            />
          </View>

          {/* Smoke Detectors */}
          <DetectorCard
            isMobile
            iconName="smoke-detector"
            iconColor="#EF4444"
            iconBg="#FEE2E2"
            title="Smoke Detectors"
            question="Do you have Smoke Detectors on every floor of the property?"
            value={state.is_smoke_detector_available_on_every_floor}
            onChange={(val) => _handleChange({ value: val, label: 'is_smoke_detector_available_on_every_floor' })}
            showDate={state.is_smoke_detector_available_on_every_floor === true}
            dateValue={state.smoke_detector_testing_date}
            onDateChange={(e) => _handleChange({ value: e, label: 'smoke_detector_testing_date' })}
          />

          {/* Carbon Monoxide Detectors */}
          <DetectorCard
            isMobile
            iconName="molecule-co2"
            iconColor="#F59E0B"
            iconBg="#FEF3C7"
            title="Carbon Monoxide Detectors"
            question="Do you have Carbon Monoxide Detectors in every room with an appliance which burns fossil fuels?"
            value={state.is_carbon_monoxide_detector_available_in_every_room}
            onChange={(val) => _handleChange({ value: val, label: 'is_carbon_monoxide_detector_available_in_every_room' })}
            showDate={state.is_carbon_monoxide_detector_available_in_every_room === true}
            dateValue={state.carbon_monoxide_detector_testing_date}
            onDateChange={(e) => _handleChange({ value: e, label: 'carbon_monoxide_detector_testing_date' })}
          />

          {/* Habitation Requirements */}
          <View style={ms.sectionCard}>
            <Text style={ms.sectionLabel}>HABITATION REQUIREMENTS</Text>
            <HabitationRow
              label="Covered by a Policy or Local Standard"
              required
              value={state.is_cleaned_to_professional_standard}
              onChange={(val) => _handleChange({ value: val, label: 'is_cleaned_to_professional_standard' })}
            />
            <HabitationRow
              label="Flooring Installed"
              required
              value={state.is_plumbing_checked}
              onChange={(val) => _handleChange({ value: val, label: 'is_plumbing_checked' })}
            />
            <HabitationRow
              label="Appliances Checked"
              required
              value={state.appliances_checked}
              onChange={(val) => _handleChange({ value: val, label: 'appliances_checked' })}
              isLast
            />
          </View>

          <View style={{ height: 100 }} />
        </ScrollView>

        {/* Sticky footer */}
        <View style={ms.footer}>
          <Text style={ms.footerNote}>All fields marked * are required</Text>
          <TouchableOpacity style={ms.footerBtn} onPress={_onSave} activeOpacity={0.85}>
            <Text style={ms.footerBtnText}>Save and Continue →</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  /* ════════════════════════════════════════
     WEB / TABLET LAYOUT
  ════════════════════════════════════════ */
  return (
    <View style={{ flex: 1, position: 'relative', backgroundColor: '#F5F7FB' }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Breadcrumb */}
        <PropertyBreadcrumb
          navigation={navigation}
          propertyId={activePropertyId}
          propertyName={propertyDisplayName}
          currentStep="Requirements"
        />

        {/* Header Banner Card */}
        <PropertyHeaderBanner
          icon="list-outline"
          title="Property Requirements"
          subtitle="Please fill in all the necessary requirements for the property."
          isMobile={isMobile}
          rightComponent={
            activePropertyId ? (
              <MarkAsComplete
                label={'Property requirements'}
                flowID={activePropertyId}
                flowtype={routeParams?.key}
                isComplete={routeParams?.status === 2}
              />
            ) : null
          }
        />

        {/* Body */}
        <View style={ws.body}>
          {/* Main card */}
          <View style={ws.mainCard}>
            {/* Smoke Detectors */}
            <DetectorCard
              isMobile={false}
              iconName="smoke-detector"
              iconColor="#EF4444"
              iconBg="#FEE2E2"
              title="Smoke Detectors"
              question="Do you have Smoke Detectors on every floor of the property?"
              value={state.is_smoke_detector_available_on_every_floor}
              onChange={(val) => _handleChange({ value: val, label: 'is_smoke_detector_available_on_every_floor' })}
              showDate={state.is_smoke_detector_available_on_every_floor === true}
              dateValue={state.smoke_detector_testing_date}
              onDateChange={(e) => _handleChange({ value: e, label: 'smoke_detector_testing_date' })}
            />

            {/* Carbon Monoxide Detectors */}
            <DetectorCard
              isMobile={false}
              iconName="molecule-co2"
              iconColor="#F59E0B"
              iconBg="#FEF3C7"
              title="Carbon Monoxide Detectors"
              question="Do you have Carbon Monoxide Detectors in every room with an appliance which burns fossil fuels?"
              value={state.is_carbon_monoxide_detector_available_in_every_room}
              onChange={(val) => _handleChange({ value: val, label: 'is_carbon_monoxide_detector_available_in_every_room' })}
              showDate={state.is_carbon_monoxide_detector_available_in_every_room === true}
              dateValue={state.carbon_monoxide_detector_testing_date}
              onDateChange={(e) => _handleChange({ value: e, label: 'carbon_monoxide_detector_testing_date' })}
            />

            {/* Habitation Requirements */}
            <View style={ws.habitationSection}>
              <Text style={ws.sectionLabel}>HABITATION REQUIREMENTS</Text>
              <View style={ws.habitationTable}>
                <HabitationRow
                  label="Covered by a Policy or Local Standard"
                  required
                  value={state.is_cleaned_to_professional_standard}
                  onChange={(val) => _handleChange({ value: val, label: 'is_cleaned_to_professional_standard' })}
                />
                <HabitationRow
                  label="Flooring Installed"
                  required
                  value={state.is_plumbing_checked}
                  onChange={(val) => _handleChange({ value: val, label: 'is_plumbing_checked' })}
                />
                <HabitationRow
                  label="Appliances Checked"
                  required
                  value={state.appliances_checked}
                  onChange={(val) => _handleChange({ value: val, label: 'appliances_checked' })}
                  isLast
                />
              </View>
            </View>
          </View>

          {/* Sidebar */}
          <SetupSidebar
            activeKey="requirements"
            propertyId={activePropertyId}
            propertyName={propertyDisplayName}
            navigation={navigation}
          />
        </View>
      </ScrollView>

      {/* Sticky footer */}
      <PropertyStickyFooter onSave={_onSave} isMobile={isMobile} />
    </View>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════ */

/* ── MOBILE ── */
const ms = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },
  scroll: { padding: 16, paddingBottom: 30 },

  progressCard: { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 12, ...SHADOWS.sm },
  progressRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  progressTitle: { fontSize: 14, fontWeight: '700', color: '#111827' },
  progressStep: { fontSize: 12, color: '#6C2BD9', fontWeight: '700' },
  track: { height: 6, backgroundColor: '#F0E8FF', borderRadius: 3, overflow: 'hidden' },
  fill: { width: '62%', height: '100%', backgroundColor: '#6C2BD9', borderRadius: 3 },

  headerCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, ...SHADOWS.sm },
  headerIcon: { width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#111827' },
  headerSub: { fontSize: 12, color: '#9CA3AF', marginTop: 3, lineHeight: 17 },

  completeCard: { backgroundColor: '#fff', borderRadius: 14, paddingHorizontal: 16, paddingVertical: 4, marginBottom: 12, ...SHADOWS.sm },

  sectionCard: { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 16, ...SHADOWS.sm },
  sectionLabel: { fontSize: 11, fontWeight: '700', color: '#9CA3AF', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 },

  footer: { backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, ...SHADOWS.md },
  footerNote: { fontSize: 12, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 20, paddingVertical: 12 },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

/* ── WEB / TABLET ── */
const ws = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },

  breadcrumbBar: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 24, paddingVertical: 14, flexWrap: 'wrap' },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 4 },
  backText: { fontSize: 13, color: '#6C2BD9', fontWeight: '600' },
  /* Banner Card */
  bannerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    ...SHADOWS.sm,
  },
  bannerCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    width: '100%',
  },
  bannerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bannerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  bannerSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  bannerRight: {},

  body: { flexDirection: 'row', alignItems: 'flex-start', gap: 20, paddingHorizontal: 24, paddingBottom: 100 },

  mainCard: { flex: 1, backgroundColor: '#fff', borderRadius: 16, padding: 24, ...SHADOWS.md },

  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 24, paddingBottom: 20, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', flexWrap: 'wrap' },
  cardIconWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardTitle: { fontSize: 20, fontWeight: '700', color: '#111827' },
  cardSub: { fontSize: 13, color: '#9CA3AF', marginTop: 3 },

  habitationSection: { marginTop: 8, paddingTop: 20, borderTopWidth: 1, borderTopColor: '#F3F4F6' },
  sectionLabel: { fontSize: 11, fontWeight: '700', color: '#9CA3AF', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 },
  habitationTable: { borderRadius: 10, borderWidth: 1, borderColor: '#F3F4F6', paddingHorizontal: 14 },

  stickyFooter: { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 24, paddingVertical: 14, zIndex: 1000, ...SHADOWS.md },
  footerNote: { fontSize: 13, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 24, paddingVertical: 12 },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

export default drawerHoc({
  Component: Requirements,
  showBackButton: true,
  noScroll: true,
});
